import React, { useCallback, useContext, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import {
  AccessTimeRounded,
  CheckCircleOutlineRounded,
  CloseRounded,
  ErrorOutlineRounded,
  ExpandMoreRounded,
  InfoOutlined,
  PersonOutlineRounded,
  RefreshRounded,
  ShieldOutlined,
  SyncRounded,
  WarningAmberRounded,
} from "@mui/icons-material";

import {
  SincronizacaoFaturamentoContext,
  TIPOS_SINCRONIZACAO,
  obterMensagemErro,
} from "../../Context/SincronizacaoFaturamentoContext";
import { solicitarSincronizacaoIndividual } from "../../services/sincronizacaoFaturamento";

import "./styles.css";

const SITUACOES_PENDENTES = [
  "PENDENTE_NA_ORIGEM",
  "ARQUIVO_TISS_INDISPONIVEL",
  "NAO_LOCALIZADO_NA_ORIGEM",
];

const formatadorDataHora = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "short",
  timeStyle: "medium",
  timeZone: "America/Sao_Paulo",
});

const formatadorDataCivilFallback = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "short",
  timeZone: "America/Sao_Paulo",
});

const formatadorMoeda = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const AVISO_CONSULTA_TEMPORAL =
  "A atualização considera a data de envio registrada no SoftLab dentro da competência selecionada. Lotes sem registro de envio não puderam ser associados com segurança ao intervalo e não foram contabilizados.";

const CONFIGURACAO_ITENS = [
  {
    chave: "pendentes",
    titulo: "Pendentes na origem",
    descricao:
      "Regularize o envio ou o arquivo TISS no SoftLab e verifique o lote novamente.",
    classe: "pendente",
    situacoes: SITUACOES_PENDENTES,
    Icone: WarningAmberRounded,
    expandido: true,
  },
  {
    chave: "erros",
    titulo: "Erros da plataforma",
    descricao: "Estes lotes não puderam ser processados por uma falha técnica.",
    classe: "erro",
    situacoes: ["ERRO_TECNICO"],
    Icone: ErrorOutlineRounded,
    expandido: true,
  },
  {
    chave: "preservados",
    titulo: "Preservados",
    descricao:
      "Os valores alterados manualmente foram mantidos e não foram substituídos.",
    classe: "preservado",
    situacoes: ["PRESERVADO_POR_AJUSTE_MANUAL"],
    Icone: ShieldOutlined,
    expandido: true,
  },
  {
    chave: "processados",
    titulo: "Processados",
    descricao: "Lotes processados e atualizados.",
    classe: "processado",
    situacoes: ["PROCESSADO"],
    Icone: CheckCircleOutlineRounded,
    expandido: false,
  },
];

const temPropriedade = (objeto, propriedade) =>
  Object.prototype.hasOwnProperty.call(objeto ?? {}, propriedade);

const pluralizar = (quantidade, singular, plural) =>
  `${quantidade} ${Number(quantidade) === 1 ? singular : plural}`;

const formatarData = (valor) => {
  if (!valor) {
    return "";
  }

  const partesDataHoraSemFuso = String(valor).match(
    /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?$/,
  );

  if (partesDataHoraSemFuso) {
    const [, ano, mes, dia, hora, minuto, segundo] = partesDataHoraSemFuso;
    return `${dia}/${mes}/${ano} às ${hora}:${minuto}:${segundo}`;
  }

  const data = new Date(valor);

  if (Number.isNaN(data.getTime())) {
    return String(valor);
  }

  return formatadorDataHora.format(data).replace(", ", " às ");
};

const formatarDatasEmTexto = (texto) =>
  String(texto ?? "").replace(
    /(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?Z?/g,
    (_data, ano, mes, dia, hora, minuto, segundo) =>
      `${dia}/${mes}/${ano} às ${hora}:${minuto}:${segundo}`,
  );

const formatarDataCivil = (valor) => {
  if (!valor) {
    return "";
  }

  const partesDataSemFuso = String(valor).match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s]\d{2}:\d{2}:\d{2}(?:\.\d+)?)?$/,
  );

  if (partesDataSemFuso) {
    const [, ano, mes, dia] = partesDataSemFuso;
    return `${dia}/${mes}/${ano}`;
  }

  const data = new Date(valor);

  return Number.isNaN(data.getTime())
    ? String(valor)
    : formatadorDataCivilFallback.format(data);
};

const formatarResumoCompetencia = (inicio, fim) => {
  if (!/^\d{2}\/\d{4}$/.test(String(inicio ?? ""))) {
    return "";
  }

  if (!/^\d{2}\/\d{4}$/.test(String(fim ?? ""))) {
    return "";
  }

  return inicio === fim ? inicio : `${inicio} a ${fim}`;
};

const obterCompetenciaDoPeriodo = (inicio, fim) => {
  const inicioPartes = String(inicio ?? "").match(
    /^(\d{4})-(\d{2})-(\d{2})/,
  );
  const fimPartes = String(fim ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (!inicioPartes || !fimPartes || inicioPartes[3] !== "01") {
    return "";
  }

  const ultimoDia = String(
    new Date(
      Date.UTC(Number(fimPartes[1]), Number(fimPartes[2]), 0),
    ).getUTCDate(),
  ).padStart(2, "0");

  if (fimPartes[3] !== ultimoDia) {
    return "";
  }

  return formatarResumoCompetencia(
    `${inicioPartes[2]}/${inicioPartes[1]}`,
    `${fimPartes[2]}/${fimPartes[1]}`,
  );
};

const formatarDuracao = (execucao) => {
  const duracaoInformada = Number(
    execucao?.duracao_segundos ?? execucao?.duracao,
  );
  const inicio = new Date(execucao?.iniciado_em).getTime();
  const fim = new Date(execucao?.finalizado_em).getTime();
  const duracao = Number.isFinite(duracaoInformada)
    ? duracaoInformada
    : (fim - inicio) / 1000;

  if (!Number.isFinite(duracao) || duracao < 0) {
    return "";
  }

  const segundos = Math.round(duracao);
  const minutos = Math.floor(segundos / 60);
  const segundosRestantes = segundos % 60;

  return minutos > 0
    ? `${minutos} min ${String(segundosRestantes).padStart(2, "0")} s`
    : `${segundos} s`;
};

const obterFiltros = (execucao) => execucao.filtros ?? {};

const obterEscopo = (execucao) => {
  const filtros = obterFiltros(execucao);

  if (execucao.tipo === TIPOS_SINCRONIZACAO.SINCRONIZACAO_INDIVIDUAL) {
    return `Lote ${
      execucao.codigo_lote ??
      execucao.lote ??
      filtros.codigo_lote ??
      "não informado"
    } · Convênio ${
      execucao.convenio ??
      execucao.plano ??
      filtros.convenio ??
      "não informado"
    }`;
  }

  const inicio = formatarDataCivil(
    execucao.data_inicio_filtro ??
      execucao.dataInicio ??
      filtros.data_inicio,
  );
  const fim = formatarDataCivil(
    execucao.data_fim_filtro ?? execucao.dataFim ?? filtros.data_fim,
  );
  const competencia = formatarResumoCompetencia(
    execucao.competencia_inicial ?? filtros.competencia_inicial,
    execucao.competencia_final ?? filtros.competencia_final,
  );
  const competenciaInferida = obterCompetenciaDoPeriodo(
    execucao.data_inicio_filtro ?? execucao.dataInicio ?? filtros.data_inicio,
    execucao.data_fim_filtro ?? execucao.dataFim ?? filtros.data_fim,
  );
  const periodo = competencia
    ? `Competência ${competencia}`
    : competenciaInferida
      ? `Competência ${competenciaInferida}`
      : inicio && fim
        ? `Período de envio: ${inicio} a ${fim}`
        : "Período não informado";
  const lote =
    execucao.codigo_lote ?? execucao.lote ?? filtros.codigo_lote;

  return lote ? `${periodo} · Lote contendo “${lote}”` : periodo;
};

const obterTipo = (execucao) =>
  execucao.tipo === TIPOS_SINCRONIZACAO.SINCRONIZACAO_INDIVIDUAL
    ? "Sincronização individual"
    : "Atualização geral";

const formatarCamposAlterados = (campos) => {
  if (!campos) {
    return "";
  }

  if (Array.isArray(campos)) {
    return campos
      .map((campo) =>
        typeof campo === "string"
          ? campo
          : campo?.campo ?? campo?.nome ?? JSON.stringify(campo),
      )
      .join(", ");
  }

  if (typeof campos === "object") {
    return Object.keys(campos).join(", ");
  }

  return String(campos);
};

const obterItens = (execucao) =>
  Array.isArray(execucao.itens) ? execucao.itens : [];

const obterObservacoes = (execucao) => {
  const observacoesRecebidas = Array.isArray(execucao.observacoes)
    ? execucao.observacoes.filter(Boolean)
    : [];
  const filtros = obterFiltros(execucao);
  const codigoLote =
    filtros.codigo_lote ?? execucao.codigo_lote ?? execucao.lote;
  const possuiPeriodo = Boolean(
    (filtros.data_inicio ?? execucao.data_inicio_filtro ?? execucao.dataInicio) &&
      (filtros.data_fim ?? execucao.data_fim_filtro ?? execucao.dataFim),
  );
  const observacoes = [...observacoesRecebidas];

  if (
    codigoLote &&
    !observacoes.some((observacao) => /filtro de lote.+parcial/i.test(observacao))
  ) {
    observacoes.unshift(
      `O filtro de lote "${codigoLote}" é parcial; códigos que contêm esse trecho também podem ser localizados.`,
    );
  }

  if (
    possuiPeriodo &&
    !codigoLote &&
    !observacoes.includes(AVISO_CONSULTA_TEMPORAL)
  ) {
    observacoes.unshift(AVISO_CONSULTA_TEMPORAL);
  }

  return observacoes.map(formatarDatasEmTexto);
};

const obterSituacao = (execucao) => {
  if (execucao.situacao) {
    return execucao.situacao;
  }

  if (
    execucao.status === "FALHOU" &&
    Number(execucao.total_erro) === 0 &&
    /lote.*n[aã]o encontr|n[aã]o localizado.*softlab/i.test(
      execucao.mensagem ?? "",
    )
  ) {
    return "NAO_LOCALIZADO_NA_ORIGEM";
  }

  if (Number(execucao.total_erro) > 0) {
    return "COM_ERROS";
  }

  if (
    Number(execucao.total_pendente_origem) > 0 ||
    Number(execucao.total_preservado) > 0
  ) {
    return "COM_PENDENCIAS";
  }

  return execucao.status;
};

const ehSituacaoPendente = (situacao) =>
  situacao === "COM_PENDENCIAS" || SITUACOES_PENDENTES.includes(situacao);

const obterApresentacao = (execucao) => {
  const situacao = obterSituacao(execucao);
  const pendentes = Number(execucao.total_pendente_origem);
  const preservados = Number(execucao.total_preservado);
  const erros = Number(execucao.total_erro);

  if (execucao.status === "EM_ANDAMENTO") {
    return {
      rotulo: execucao.otimista ? "Iniciando" : "Em andamento",
      classe: "em-andamento",
      Icone: SyncRounded,
      resumo: "Aguarde enquanto os dados dos lotes são atualizados.",
      descricao:
        "Você pode continuar usando a página. O resultado aparecerá aqui quando a atualização terminar.",
      acao: "Acompanhar",
    };
  }

  if (ehSituacaoPendente(situacao)) {
    let resumo = "Há lotes que precisam de atenção no SoftLab.";

    if (pendentes > 0) {
      resumo = `${pluralizar(
        pendentes,
        "lote precisa",
        "lotes precisam",
      )} de atenção no SoftLab.`;
    } else if (preservados > 0) {
      resumo = `${pluralizar(
        preservados,
        "ajuste manual foi preservado",
        "ajustes manuais foram preservados",
      )}.`;
    }

    return {
      rotulo: "Concluída com pendências",
      classe: "com-pendencias",
      Icone: WarningAmberRounded,
      resumo,
      descricao:
        pendentes > 0
          ? "As pendências são da origem e não representam uma falha da plataforma. Consulte o motivo antes de verificar novamente."
          : "Os valores alterados manualmente foram mantidos. Consulte os lotes preservados para entender o resultado.",
      acao: "Ver pendências",
    };
  }

  if (
    execucao.status === "FALHOU" ||
    situacao === "COM_ERROS" ||
    situacao === "ERRO_TECNICO"
  ) {
    return {
      rotulo: execucao.status === "FALHOU" ? "Falhou" : "Concluída com erros",
      classe: "falhou",
      Icone: ErrorOutlineRounded,
      resumo:
        erros > 0
          ? `${pluralizar(erros, "lote apresentou", "lotes apresentaram")} erro da plataforma.`
          : "Não foi possível concluir esta sincronização.",
      descricao:
        "Consulte os detalhes da falha. Se o problema continuar, acione o suporte.",
      acao: "Ver erro",
    };
  }

  return {
    rotulo: "Concluída",
    classe: "concluida",
    Icone: CheckCircleOutlineRounded,
    resumo: execucao.mensagem || "Lotes processados e atualizados.",
    descricao: "A sincronização foi concluída sem pendências ou erros.",
    acao: "Ver resultado",
  };
};

const obterContadores = (execucao) => {
  const contadores = [
    {
      propriedade: "total_encontrado",
      rotulo: "Encontrados",
      classe: "neutro",
    },
    {
      propriedade: "total_processado",
      rotulo: "Processados",
      classe: "processado",
    },
    {
      propriedade: "total_preservado",
      rotulo: "Preservados",
      classe: "preservado",
    },
    {
      propriedade: "total_pendente_origem",
      rotulo: "Pendentes na origem",
      classe: "pendente",
    },
    {
      propriedade: "total_erro",
      rotulo: "Erros da plataforma",
      classe: "erro",
    },
  ];

  if (
    temPropriedade(execucao, "total_ignorado") &&
    !temPropriedade(execucao, "total_preservado") &&
    !temPropriedade(execucao, "total_pendente_origem")
  ) {
    contadores.splice(2, 0, {
      propriedade: "total_ignorado",
      rotulo: "Ignorados",
      classe: "neutro",
    });
  }

  return contadores
    .filter(({ propriedade }) => temPropriedade(execucao, propriedade))
    .map(({ propriedade, ...contador }) => ({
      ...contador,
      valor: execucao[propriedade],
    }));
};

const agruparItens = (itens) => {
  const situacoesConhecidas = new Set(
    CONFIGURACAO_ITENS.flatMap(({ situacoes }) => situacoes),
  );
  const grupos = CONFIGURACAO_ITENS.map((configuracao) => ({
    ...configuracao,
    itens: itens.filter((item) =>
      configuracao.situacoes.includes(item?.situacao),
    ),
  })).filter(({ itens: itensDoGrupo }) => itensDoGrupo.length > 0);
  const outrosItens = itens.filter(
    (item) => !situacoesConhecidas.has(item?.situacao),
  );

  if (outrosItens.length > 0) {
    grupos.push({
      chave: "outros",
      titulo: "Outros resultados",
      descricao: "Informações adicionais retornadas pela sincronização.",
      classe: "neutro",
      Icone: InfoOutlined,
      expandido: true,
      itens: outrosItens,
    });
  }

  return grupos;
};

const obterIdentificadorItem = (item, indice = 0) =>
  item?.id_lote !== undefined && item?.id_lote !== null
    ? `id-lote:${item.id_lote}`
    : `sem-identificador:${indice}`;

const formatarQuantidade = (quantidade) => {
  if (quantidade === null) {
    return "não calculado";
  }

  return Number.isFinite(Number(quantidade))
    ? Number(quantidade).toLocaleString("pt-BR")
    : String(quantidade);
};

const formatarValor = (valor) => {
  if (valor === null) {
    return "não calculado";
  }

  return Number.isFinite(Number(valor))
    ? formatadorMoeda.format(Number(valor))
    : String(valor);
};

function Metadado({ icone, rotulo, valor }) {
  if (!valor) {
    return null;
  }

  return (
    <div className="status-sincronizacao__metadado">
      <span className="status-sincronizacao__metadado-icone">{icone}</span>
      <div>
        <span className="status-sincronizacao__metadado-rotulo">{rotulo}</span>
        <strong className="status-sincronizacao__metadado-valor">
          {valor}
        </strong>
      </div>
    </div>
  );
}

function ItemResultado({
  item,
  classe,
  pendente,
  verificando,
  sincronizacaoAtiva,
  aoVerificarNovamente,
}) {
  const codigo = item.codigo ?? item.codigo_lote ?? "não informado";
  const convenio = item.convenio ?? item.plano;
  const identificador = item.id_lote ?? "não informado";
  const podeVerificar = Boolean(
    pendente && codigo !== "não informado" && convenio && aoVerificarNovamente,
  );
  const motivo =
    item.motivo ??
    (classe === "processado"
      ? "Lote processado e atualizado."
      : classe === "preservado"
        ? "Lote preservado: o valor foi alterado manualmente e não foi substituído."
        : classe === "erro"
          ? "Não foi possível processar este lote por uma falha técnica."
          : "Não foi possível obter mais detalhes deste lote.");

  return (
    <article
      className={`status-sincronizacao__item status-sincronizacao__item--${classe}`}
    >
      <div className="status-sincronizacao__item-cabecalho">
        <strong className="status-sincronizacao__item-titulo">
          Lote {codigo} — Convênio {convenio || "não informado"} — Identificador{" "}
          {identificador}
        </strong>
      </div>

      <p className="status-sincronizacao__item-motivo">{motivo}</p>

      {temPropriedade(item, "quantidade") ||
      temPropriedade(item, "valor") ||
      item.data_envio_lote ? (
        <div className="status-sincronizacao__item-informacoes">
          {item.data_envio_lote && (
            <span>
              <strong>Data de envio:</strong> {formatarData(item.data_envio_lote)}
            </span>
          )}
          {temPropriedade(item, "quantidade") && (
            <span>
              <strong>Quantidade:</strong> {formatarQuantidade(item.quantidade)}
            </span>
          )}
          {temPropriedade(item, "valor") && (
            <span>
              <strong>Valor:</strong> {formatarValor(item.valor)}
            </span>
          )}
        </div>
      ) : null}

      {podeVerificar && (
        <div className="status-sincronizacao__item-acao">
          <Button
            size="small"
            variant="outlined"
            startIcon={
              verificando || sincronizacaoAtiva ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <RefreshRounded fontSize="small" />
              )
            }
            disabled={verificando || sincronizacaoAtiva}
            onClick={() => aoVerificarNovamente(item)}
          >
            {verificando || sincronizacaoAtiva
              ? "Verificando..."
              : "Verificar novamente"}
          </Button>
        </div>
      )}
    </article>
  );
}

function ObservacoesConsulta({ observacoes }) {
  if (observacoes.length === 0) {
    return null;
  }

  return (
    <aside className="status-sincronizacao__observacoes">
      <InfoOutlined fontSize="small" aria-hidden="true" />
      <div>
        <strong>Observações sobre a consulta</strong>
        <ul>
          {observacoes.map((observacao, indice) => (
            <li key={`observacao-${indice}`}>{observacao}</li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

function DetalhesExecucao({
  execucao,
  aberto,
  aoFechar,
  itensEmVerificacao,
  obterSincronizacaoLoteAtiva,
  aoVerificarNovamente,
}) {
  const apresentacao = obterApresentacao(execucao);
  const duracao = formatarDuracao(execucao);
  const camposAlterados = formatarCamposAlterados(execucao.campos_alterados);
  const contadores = obterContadores(execucao);
  const grupos = agruparItens(obterItens(execucao));
  const observacoes = obterObservacoes(execucao);
  const possuiItensDetalhados = grupos.length > 0;
  const { Icone } = apresentacao;

  return (
    <Dialog
      open={aberto}
      onClose={aoFechar}
      fullWidth
      maxWidth="md"
      transitionDuration={0}
      aria-labelledby="titulo-detalhes-sincronizacao"
    >
      <DialogTitle
        id="titulo-detalhes-sincronizacao"
        className="status-sincronizacao__modal-titulo"
      >
        Resultado da {obterTipo(execucao).toLowerCase()}
        <IconButton
          size="small"
          onClick={aoFechar}
          aria-label="Fechar detalhes da sincronização"
        >
          <CloseRounded fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className="status-sincronizacao__modal-conteudo">
        {!possuiItensDetalhados && (
          <div
            className={`status-sincronizacao__resultado status-sincronizacao__resultado--${apresentacao.classe}`}
          >
            <span className="status-sincronizacao__resultado-icone" aria-hidden="true">
              <Icone />
            </span>
            <div>
              <strong>{apresentacao.resumo}</strong>
              <p>{apresentacao.descricao}</p>
            </div>
          </div>
        )}

        <Typography
          component="p"
          className="status-sincronizacao__modal-escopo"
        >
          <strong>Escopo consultado:</strong> {obterEscopo(execucao)}
        </Typography>

        {contadores.length > 0 && (
          <div
            className="status-sincronizacao__metricas"
            aria-label="Resumo da sincronização"
          >
            {contadores.map(({ rotulo, valor, classe }) => (
              <div
                className={`status-sincronizacao__metrica status-sincronizacao__metrica--${classe}`}
                key={rotulo}
              >
                <span className="status-sincronizacao__metrica-rotulo">
                  {rotulo}
                </span>
                <strong className="status-sincronizacao__metrica-valor">
                  {valor === null || valor === undefined ? "—" : valor}
                </strong>
              </div>
            ))}
          </div>
        )}

        {grupos.length > 0 && (
          <div className="status-sincronizacao__grupos">
            {grupos.map(({ Icone: IconeGrupo, ...grupo }) => (
              <Accordion
                key={grupo.chave}
                defaultExpanded={grupo.expandido}
                disableGutters
                className={`status-sincronizacao__grupo status-sincronizacao__grupo--${grupo.classe}`}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreRounded />}
                  aria-controls={`conteudo-grupo-${grupo.chave}`}
                  id={`cabecalho-grupo-${grupo.chave}`}
                >
                  <span className="status-sincronizacao__grupo-icone" aria-hidden="true">
                    <IconeGrupo fontSize="small" />
                  </span>
                  <span className="status-sincronizacao__grupo-cabecalho">
                    <strong>{grupo.titulo}</strong>
                    <small>{grupo.descricao}</small>
                  </span>
                  <Chip size="small" label={grupo.itens.length} />
                </AccordionSummary>
                <AccordionDetails id={`conteudo-grupo-${grupo.chave}`}>
                  <div className="status-sincronizacao__lista-itens">
                    {grupo.itens.map((item, indice) => {
                      const identificador = obterIdentificadorItem(item, indice);
                      const sincronizacaoAtiva = obterSincronizacaoLoteAtiva?.(
                        item.codigo ?? item.codigo_lote,
                        item.convenio ?? item.plano,
                        item.id_lote,
                      );

                      return (
                        <ItemResultado
                          key={identificador}
                          item={item}
                          classe={grupo.classe}
                          pendente={grupo.chave === "pendentes"}
                          verificando={itensEmVerificacao.has(identificador)}
                          sincronizacaoAtiva={Boolean(sincronizacaoAtiva)}
                          aoVerificarNovamente={(itemSelecionado) =>
                            aoVerificarNovamente(itemSelecionado, identificador)
                          }
                        />
                      );
                    })}
                  </div>
                </AccordionDetails>
              </Accordion>
            ))}
          </div>
        )}

        <ObservacoesConsulta observacoes={observacoes} />

        {camposAlterados && (
          <Typography
            component="p"
            className="status-sincronizacao__modal-detalhe"
          >
            <strong>Campos alterados:</strong> {camposAlterados}
          </Typography>
        )}

        <details className="status-sincronizacao__detalhes-tecnicos">
          <summary>Informações da execução</summary>
          <div className="status-sincronizacao__modal-metadados">
            <Metadado
              icone={<PersonOutlineRounded fontSize="small" />}
              rotulo="Solicitante"
              valor={execucao.usuario_solicitante}
            />
            <Metadado
              icone={<AccessTimeRounded fontSize="small" />}
              rotulo="Início"
              valor={formatarData(execucao.iniciado_em)}
            />
            <Metadado
              icone={<AccessTimeRounded fontSize="small" />}
              rotulo="Duração"
              valor={duracao}
            />
          </div>
          {!possuiItensDetalhados && execucao.mensagem && (
            <Typography
              component="p"
              className="status-sincronizacao__modal-mensagem"
            >
              {execucao.mensagem}
            </Typography>
          )}
        </details>
      </DialogContent>

    </Dialog>
  );
}

function ResumoExecucao({ execucao, aoVerDetalhes, aoDispensar }) {
  const apresentacao = obterApresentacao(execucao);
  const { Icone } = apresentacao;

  return (
    <Box
      className={`status-sincronizacao__resumo status-sincronizacao__resumo--${apresentacao.classe}`}
      role="status"
    >
      <span className="status-sincronizacao__icone" aria-hidden="true">
        {execucao.status === "EM_ANDAMENTO" ? (
          <CircularProgress size={17} thickness={5} color="inherit" />
        ) : (
          <Icone fontSize="small" />
        )}
      </span>

      <div className="status-sincronizacao__identificacao">
        <div className="status-sincronizacao__titulo-linha">
          <Typography component="h2" className="status-sincronizacao__titulo">
            {obterTipo(execucao)}
          </Typography>
          <Chip
            size="small"
            label={apresentacao.rotulo}
            className="status-sincronizacao__selo"
          />
        </div>
        <Typography component="p" className="status-sincronizacao__mensagem-resumo">
          {apresentacao.resumo}
        </Typography>
        <Typography component="p" className="status-sincronizacao__escopo">
          {obterEscopo(execucao)}
        </Typography>
      </div>

      <div className="status-sincronizacao__acoes">
        <Button size="small" onClick={aoVerDetalhes}>
          {apresentacao.acao}
        </Button>
        {aoDispensar && (
          <IconButton
            size="small"
            onClick={aoDispensar}
            className="status-sincronizacao__dispensar"
            aria-label="Dispensar status da sincronização"
            title="Dispensar este aviso"
          >
            <CloseRounded fontSize="small" />
          </IconButton>
        )}
      </div>
    </Box>
  );
}

export default function StatusSincronizacao() {
  const { enqueueSnackbar } = useSnackbar();
  const [detalhesAbertos, setDetalhesAbertos] = useState(false);
  const [itensEmVerificacao, setItensEmVerificacao] = useState(new Set());
  const {
    execucoesAtivas,
    ultimaExecucaoFinalizadaVisivel,
    erroAcompanhamento,
    dispensarExecucao,
    obterSincronizacaoLoteAtiva,
    registrarInicio,
    cancelarInicio,
    registrarResultado,
    registrarFalha,
  } = useContext(SincronizacaoFaturamentoContext);

  const execucaoExibida =
    execucoesAtivas[0] ?? ultimaExecucaoFinalizadaVisivel;

  const itensDaExecucao = useMemo(
    () => obterItens(execucaoExibida ?? {}),
    [execucaoExibida],
  );

  const verificarNovamente = useCallback(
    async (item, identificador) => {
      const codigo = item.codigo ?? item.codigo_lote;
      const convenio = item.convenio ?? item.plano;

      if (!codigo || !convenio || itensEmVerificacao.has(identificador)) {
        return;
      }

      if (obterSincronizacaoLoteAtiva?.(codigo, convenio, item.id_lote)) {
        enqueueSnackbar("Este lote já está sendo verificado.", {
          variant: "info",
        });
        return;
      }

      setItensEmVerificacao((itensAtuais) =>
        new Set(itensAtuais).add(identificador),
      );
      const execucaoOtimista = registrarInicio({
        tipo: TIPOS_SINCRONIZACAO.SINCRONIZACAO_INDIVIDUAL,
        id_lote: item.id_lote ?? null,
        codigo_lote: codigo,
        convenio,
        mensagem: "Verificando novamente o lote...",
      });

      try {
        const resposta = await solicitarSincronizacaoIndividual({
          idLote: item.id_lote,
          lote: codigo,
          convenio,
        });
        const execucao = registrarResultado(resposta, execucaoOtimista);
        const situacao = obterSituacao(execucao ?? {});
        const continuaPendente = ehSituacaoPendente(situacao);

        enqueueSnackbar(
          execucao?.mensagem ??
            (continuaPendente
              ? "O lote continua pendente na origem. Consulte o motivo atualizado."
              : "Lote verificado e atualizado com sucesso."),
          { variant: continuaPendente ? "warning" : "success" },
        );
      } catch (erro) {
        const statusHttp = erro?.response?.status;

        if (statusHttp === 409 || statusHttp === 400) {
          cancelarInicio(execucaoOtimista);
          enqueueSnackbar(
            obterMensagemErro(
              erro,
              statusHttp === 409
                ? "Este lote já possui uma sincronização em andamento."
                : "Não foi possível verificar o lote com os dados informados.",
            ),
            { variant: "warning" },
          );
          return;
        }

        registrarFalha(erro, execucaoOtimista);
        enqueueSnackbar(
          obterMensagemErro(
            erro,
            statusHttp === 404
              ? "O lote ainda não foi localizado no SoftLab."
              : "Não foi possível verificar o lote novamente.",
          ),
          { variant: statusHttp === 404 ? "warning" : "error" },
        );
      } finally {
        setItensEmVerificacao((itensAtuais) => {
          const proximosItens = new Set(itensAtuais);
          proximosItens.delete(identificador);
          return proximosItens;
        });
      }
    },
    [
      cancelarInicio,
      enqueueSnackbar,
      itensEmVerificacao,
      obterSincronizacaoLoteAtiva,
      registrarFalha,
      registrarInicio,
      registrarResultado,
    ],
  );

  if (!execucaoExibida && !erroAcompanhamento) {
    return null;
  }

  if (!execucaoExibida) {
    return (
      <div className="status-sincronizacao__indisponivel" role="alert">
        <InfoOutlined fontSize="small" aria-hidden="true" />
        <span>{erroAcompanhamento}</span>
      </div>
    );
  }

  const execucaoFinalizada = execucaoExibida.status !== "EM_ANDAMENTO";

  return (
    <section
      className="status-sincronizacao"
      aria-label="Acompanhamento da sincronização"
      aria-live="polite"
    >
      <ResumoExecucao
        execucao={execucaoExibida}
        aoVerDetalhes={() => setDetalhesAbertos(true)}
        aoDispensar={
          execucaoFinalizada
            ? () => dispensarExecucao(execucaoExibida)
            : undefined
        }
      />
      <DetalhesExecucao
        execucao={{ ...execucaoExibida, itens: itensDaExecucao }}
        aberto={detalhesAbertos}
        aoFechar={() => setDetalhesAbertos(false)}
        itensEmVerificacao={itensEmVerificacao}
        obterSincronizacaoLoteAtiva={obterSincronizacaoLoteAtiva}
        aoVerificarNovamente={verificarNovamente}
      />
    </section>
  );
}

export {
  agruparItens,
  ehSituacaoPendente,
  obterApresentacao,
  obterContadores,
  obterSituacao,
};
